// 화면 17 — 섹터 스코어 재산정 상태 머신 + 폴링 훅.
// 권위: 사용자 결정 2026-05-15.
//
// 상태:
//   idle        — 활성 job 없음 (또는 페이지 진입 직후 초기 status 미수신)
//   submitting  — POST 진행 중
//   running     — 폴링 중 (activeJob != null). 본인이 트리거했든 타 운영자가 했든 동일.
//   completed   — 본인 트리거 jobId 가 lastCompletedJob 으로 옮겨가 status=completed
//   failed      — 본인 트리거 jobId 가 lastCompletedJob 으로 옮겨가 status=failed
//
// 폴링은 running 상태에서만 5초 간격. completed/failed 는 toast 표시 후 idle 로 복귀.
//
// 다른 운영자 동시 사용:
//   페이지 진입 시 GET /status 1회 호출. activeJob 이 있으면 jobId 모름 → running 표시만 (완료 toast 는 본인 트리거 시에만).
//
// Hot-fix 2026-05-15 (fast-path UX):
//   POST 202 직후 setInterval(5s) 시작 전에 GET /status 를 await 한다(대기 0ms).
//   - activeJob=null + lastCompletedJob.jobId==myJobId → 즉시 completed 전환 (toast 정상 표시). polling 시작 안 함.
//   - activeJob=null + lastCompletedJob.jobId!=myJobId → idle (다른 운영자 흡수). polling 시작 안 함.
//   - activeJob != null → 750ms 후 첫 polling tick, 이후 5s 정상 주기 (첫 사이클 내 완료도 캐치).
//   - GET 실패(unknown) → 750ms 킥으로 normal polling 시작.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../api/client';
import {
  getSectorRecomputeStatus,
  postSectorRecompute,
  type SectorRecomputeJob,
  type SectorRecomputeRequest,
} from '../../api/sectorRecomputeApi';

const POLL_INTERVAL_MS = 5_000;
// 즉시 완료 케이스 대응 — POST 202 직후 첫 GET /status 가 진행 중 응답이면
// 다음 호출을 5s 가 아닌 짧게(750ms) 한 번 더 시도해 fast-path(0.8s) 완료를 잡는다.
// 그 이후 정상 5s 주기.
const POLL_KICK_MS = 750;

export type RecomputePhase = 'idle' | 'submitting' | 'running' | 'completed' | 'failed';

export interface RecomputeState {
  phase: RecomputePhase;
  activeJob: SectorRecomputeJob | null;
  /** 본인 트리거 jobId — 완료/실패 매칭용. null 이면 타 운영자 트리거 (또는 미트리거). */
  myJobId: string | null;
  /** 마지막 오류 메시지 — failed phase 일 때만 의미. */
  errorMessage: string | null;
}

export interface RecomputeActions {
  /** 모달 제출 — POST 호출 후 폴링 시작. 성공 시 true, 실패(409 등) 시 false. */
  submit: (req: SectorRecomputeRequest) => Promise<{ ok: boolean; errorMessage?: string }>;
  /** completed/failed → idle 복귀 (toast 표시 후 호출). */
  acknowledge: () => void;
  /** 외부 트리거 status 강제 새로고침 (모달 진입 시). */
  refresh: () => Promise<void>;
}

export function useSectorRecompute(): RecomputeState & RecomputeActions {
  const [phase, setPhase] = useState<RecomputePhase>('idle');
  const [activeJob, setActiveJob] = useState<SectorRecomputeJob | null>(null);
  const [myJobId, setMyJobId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const timerRef = useRef<number | null>(null);
  const myJobIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);

  // 재산정 완료 시 화면 17 차트/패널 캐시 무효화 — 운영자가 새로고침 없이도 새 매핑 데이터 반영.
  // 권위: 운영자 보고 2026-05-15. useLeadingSectors.ts 의 queryKey 와 정합.
  const qc = useQueryClient();
  const invalidateLeadingSectors = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['leading-sectors'] });
  }, [qc]);

  // myJobId state ↔ ref 동기화 (인터벌 콜백에서 stale closure 회피).
  useEffect(() => {
    myJobIdRef.current = myJobId;
  }, [myJobId]);

  const stopPolling = useCallback(() => {
    if (timerRef.current !== null) {
      // setTimeout chain — clearTimeout 으로 정리. clearInterval 도 같은 id 공간이라 안전.
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // tick 반환값 — submit 이 첫 호출 결과로 polling 시작 여부 분기.
  //   'running'    — activeJob 진행 중 → 계속 polling
  //   'terminal'   — completed/failed/idle 로 전환됨 → polling 불필요
  //   'unknown'    — status 조회 실패 → 일단 polling 시작 (다음 tick 에서 회복)
  type TickOutcome = 'running' | 'terminal' | 'unknown';

  const tick = useCallback(async (): Promise<TickOutcome> => {
    try {
      const status = await getSectorRecomputeStatus();
      if (!mountedRef.current) return 'terminal';
      setActiveJob(status.activeJob);
      const mine = myJobIdRef.current;
      if (status.activeJob) {
        // 누군가 진행 중 — running 유지.
        setPhase('running');
        return 'running';
      }
      // 활성 job 없음 — 본인 트리거였다면 완료/실패 매칭.
      if (mine && status.lastCompletedJob && status.lastCompletedJob.jobId === mine) {
        if (status.lastCompletedJob.status === 'failed') {
          setErrorMessage(status.lastCompletedJob.errorMessage ?? '재산정 실패');
          setPhase('failed');
        } else {
          setPhase('completed');
          // 완료 시점에 sparkline/라인차트/divergence 캐시 무효화 → 자동 refetch.
          invalidateLeadingSectors();
        }
        stopPolling();
        return 'terminal';
      }
      // 타 운영자 트리거가 끝난 케이스 — 조용히 idle 복귀.
      setPhase('idle');
      stopPolling();
      return 'terminal';
    } catch {
      // status 폴링 일시 실패는 무시 (다음 tick 에서 회복).
      return 'unknown';
    }
  }, [stopPolling, invalidateLeadingSectors]);

  const startPolling = useCallback(
    (initialDelayMs: number = POLL_INTERVAL_MS) => {
      if (timerRef.current !== null) return;
      // 첫 호출만 짧게(initialDelayMs) — setTimeout 으로 한 번 찍고, 이후 정상 5s interval.
      // setInterval 단일 호출로 균등 5s 만 쓰면 fast-path(0.8s) 완료를 5s 까지 못 잡는다.
      timerRef.current = window.setTimeout(function pump() {
        void tick();
        if (timerRef.current === null) return; // tick 안에서 stopPolling 호출됐을 수 있음
        timerRef.current = window.setTimeout(pump, POLL_INTERVAL_MS);
      }, initialDelayMs);
    },
    [tick]
  );


  const submit = useCallback<RecomputeActions['submit']>(
    async (req) => {
      if (phase === 'submitting' || phase === 'running') {
        return { ok: false, errorMessage: '이미 진행 중입니다.' };
      }
      setPhase('submitting');
      setErrorMessage(null);
      try {
        const ack = await postSectorRecompute(req);
        if (!mountedRef.current) return { ok: true };
        setMyJobId(ack.jobId);
        myJobIdRef.current = ack.jobId;
        setPhase('running');
        // 즉시(대기 0ms) 첫 GET /status — fast-path(<1s) 완료를 잡기 위해 setInterval 시작 전 await.
        // outcome 이 terminal 이면 polling 불필요 (이미 phase=completed|failed|idle).
        // running/unknown 이면 짧은 kick(750ms) 후 정상 5s interval 시작 — 첫 5s 사이 완료도 캐치.
        const outcome = await tick();
        if (!mountedRef.current) return { ok: true };
        if (outcome !== 'terminal') {
          startPolling(POLL_KICK_MS);
        }
        return { ok: true };
      } catch (err) {
        if (!mountedRef.current) return { ok: false };
        if (err instanceof ApiError && err.httpStatus === 409) {
          // 진행 중 1건 있음 → running 으로 흡수 (본인 jobId 없음 = 타 운영자 트리거).
          setMyJobId(null);
          myJobIdRef.current = null;
          setPhase('running');
          void tick();
          startPolling();
          return { ok: false, errorMessage: '이미 다른 운영자가 재산정을 진행 중입니다.' };
        }
        const msg = err instanceof Error ? err.message : '재산정 요청 실패';
        setPhase('idle');
        setErrorMessage(msg);
        return { ok: false, errorMessage: msg };
      }
    },
    [phase, startPolling, tick]
  );

  const acknowledge = useCallback(() => {
    setPhase('idle');
    setMyJobId(null);
    myJobIdRef.current = null;
    setErrorMessage(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const status = await getSectorRecomputeStatus();
      if (!mountedRef.current) return;
      setActiveJob(status.activeJob);
      if (status.activeJob) {
        setPhase((prev) => (prev === 'submitting' ? prev : 'running'));
        startPolling();
      }
    } catch {
      // ignore
    }
  }, [startPolling]);

  // 페이지 진입 시 1회 status 조회 — 다른 운영자가 트리거한 활성 job 감지.
  useEffect(() => {
    mountedRef.current = true;
    void refresh();
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    phase,
    activeJob,
    myJobId,
    errorMessage,
    submit,
    acknowledge,
    refresh,
  };
}
